import { Helmet } from "react-helmet";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Logo from "@/components/shared/Logo";
import ThemeToggle from "@/components/layout/ThemeToggle";
import {
  CAYMAN_ELECTION_DATE,
  CAYMAN_LAUNCH_DATE,
  hasLaunchDatePassed,
  daysUntilLaunch,
} from "@/utils/date-constants";
import { toast } from "@/components/ui/use-toast";
import { getWaitlistCount } from "@/utils/supabase/waitlist"; // Updated import path

// Import refactored section components
import CaymanHeroSection from "@/components/cayman/sections/CaymanHeroSection";
import PostElectionSection from "@/components/cayman/sections/PostElectionSection";
import HowItWorksSection from "@/components/cayman/sections/HowItWorksSection";
import WhyCaymanSection from "@/components/cayman/sections/WhyCaymanSection";
import WaitlistSection from "@/components/cayman/sections/WaitlistSection";
import PostLaunchCTASection from "@/components/cayman/sections/PostLaunchCTASection";
import FAQSection from "@/components/cayman/sections/FAQSection";
import CaymanFooter from "@/components/cayman/sections/CaymanFooter";

const CaymanLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRedirectingAfterLaunch, setIsRedirectingAfterLaunch] =
    useState(false);
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);
  const [daysRemaining, setDaysRemaining] = useState<number>(daysUntilLaunch());

  // Fetch waitlist count
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      const count = await getWaitlistCount();
      setWaitlistCount(count);
    };

    fetchWaitlistCount();

    // Update the days remaining every hour (in case user stays on the page for a long time)
    const timer = setInterval(() => {
      setDaysRemaining(daysUntilLaunch());
    }, 3600000); // 1 hour

    return () => clearInterval(timer);
  }, []);

  // DISABLE automatic redirect even if launch date has passed
  // This allows the user to stay on this page without being redirected
  // Keep the effect but disable the redirect logic
  useEffect(() => {
    const previewWaitlist = location.search.includes("preview-waitlist");

    // Commented out the redirect logic to prevent automatic redirects
    /*
    if (
      hasLaunchDatePassed() &&
      !previewWaitlist &&
      !isRedirectingAfterLaunch
    ) {
      // Add a slight delay before redirecting to show the page briefly
      const timer = setTimeout(() => {
        setIsRedirectingAfterLaunch(true);
        toast({
          title: "NationCard™ Cayman Islands is now live!",
          description: "Redirecting you to the platform...",
        });
        navigate("/cards/KY", { replace: true });
      }, 1500);

      return () => clearTimeout(timer);
    }
    */
  }, [navigate, location.search, isRedirectingAfterLaunch]);

  // Update page title
  useEffect(() => {
    document.title =
      "NationCard™ Cayman Islands — Cayman, so you've voted, What now?";
  }, []);

  // Format and display the launch date nicely
  const formattedLaunchDate = CAYMAN_LAUNCH_DATE.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // FAQ data
  const faqData = [
    {
      question: "What is NationCard™?",
      answer:
        "NationCard™ is a civic engagement platform designed to keep Cayman citizens involved in the democratic process beyond election day. We provide insights, polls, and data-driven analysis of important issues affecting the Cayman Islands. You can register to become a verified pollster of the Cayman Islands to help gather data and insights.",
    },
    {
      question: "When will NationCard™ launch in the Cayman Islands?",
      answer: `We're launching on ${formattedLaunchDate}, exactly 16 days after the general election. This timing is intentional - to keep civic engagement strong even after the polls close.`,
    },
    {
      question: "How is NationCard™ different from other political platforms?",
      answer:
        "NationCard™ isn't affiliated with any political party. We're an independent platform focused on data-driven insights and citizen engagement. Our goal is transparency and accountability in government through ongoing citizen participation.",
    },
    {
      question: "Do I need to pay to use NationCard™?",
      answer:
        "No, NationCard™ is completely free for Cayman Islands citizens. We believe civic engagement should be accessible to everyone.",
    },
    {
      question: "How can I get involved beyond joining the waitlist?",
      answer:
        "Once we launch, you'll be able to participate in polls, provide insights on important issues, and even become a verified pollster to help gather data. Join our waitlist to be first to know when these opportunities become available.",
    },
  ];

  // Check if launch date has passed for conditional rendering
  const launchPassed = hasLaunchDatePassed();

  // Get the current page URL for sharing
  const pageUrl = window.location.href;
  const shareTitle = "Join me on the NationCard™ Cayman Islands waitlist";
  const shareDescription = `NationCard™ is launching in the Cayman Islands on ${formattedLaunchDate}. Join the waitlist to get early access!`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>
          NationCard™ Cayman Islands — Cayman, So you've voted, What now?
        </title>
        <meta
          name="description"
          content="Join the Cayman Islands waitlist for NationCard™ - the civic engagement platform keeping democracy alive after election day."
        />
        <meta
          property="og:title"
          content="NationCard™ Cayman Islands — Cayman, So you've voted, What now?"
        />
        <meta
          property="og:description"
          content="Join the Cayman Islands waitlist for NationCard™ - the civic engagement platform keeping democracy alive after election day."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm fixed top-0 left-0 right-0 z-50">
        <div className="container max-w-7xl mx-auto flex justify-between items-center px-4 py-3">
          {/* Updated Logo to link to the current page */}
          <Logo
            onClick={() => window.scrollTo(0, 0)}
            className="h-10 cursor-pointer"
          />
          <div className="flex items-center gap-4">
            <ThemeToggle />
            {launchPassed ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/cards/KY">Enter NationCard™</Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" asChild>
                <a href="#waitlist">Join Waitlist</a>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <CaymanHeroSection
        launchPassed={launchPassed}
        formattedLaunchDate={formattedLaunchDate}
      />

      {/* Post-Election Messaging Section */}
      <PostElectionSection />

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Why NationCard Cayman Section */}
      <WhyCaymanSection formattedLaunchDate={formattedLaunchDate} />

      {/* Waitlist Section - Only show if before launch */}
      {!launchPassed && (
        <WaitlistSection
          daysRemaining={daysRemaining}
          waitlistCount={waitlistCount}
          shareUrl={pageUrl}
          shareTitle={shareTitle}
          shareDescription={shareDescription}
        />
      )}

      {/* Post-Launch CTA - Only show if after launch */}
      {launchPassed && <PostLaunchCTASection />}

      {/* FAQ Section */}
      <FAQSection faqs={faqData} />

      {/* Footer */}
      <CaymanFooter />
    </div>
  );
};

export default CaymanLanding;
